import { Entity } from '@adornis/baseql/decorators.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { ColumnDefinitionBase } from '@adornis/data-view/ColumnDefinition.js';
import { ExportDataView } from '@adornis/data-view/db/export-data-view.js';
import type { ExamSlotRating } from '@ils/ratings/db/slotRating.js';
import { brandDictionary } from '@ils/translation/translation.js';
import { DateTime } from 'luxon';
import type { RatingsExportLine } from '../db/ratings-export.js';

const sortByOtherArray = (a: any, b: any, otherArray: any[]) => {
  const aIndex = otherArray.indexOf(a);
  const bIndex = otherArray.indexOf(b);
  return aIndex - bIndex;
};

export const formatScoresOnRatingDoc = (entityData: EntityData<RatingsExportLine>) => {
  const scores: Record<string, EntityData<ExamSlotRating>[]> = {};
  for (const rating of entityData.ratingDoc.ratings) {
    const instructionCategory = entityData.osce.instructionCategories?.find(cat =>
      cat.instructions.find(instr => instr._id === rating.instructionId),
    ) ?? { _id: 'UNKNOWN' };
    scores[instructionCategory._id] ??= [];
    scores[instructionCategory._id]!.push(rating);
  }
  return (
    '[ ' +
    Object.entries(scores)
      .sort((a, b) => sortByOtherArray(a[0], b[0], entityData.osce?.instructionCategories?.map(cat => cat._id) ?? []))
      .map(
        ([_, ratings]) =>
          '[ ' +
          ratings
            .sort((a, b) =>
              sortByOtherArray(
                a._id,
                b._id,
                entityData.osce?.instructionCategories
                  ?.find(cat => cat.instructions.find(instr => instr._id === a.instructionId))
                  ?.instructions?.map(instr => instr._id) ?? [],
              ),
            )
            .map(r => r.score)
            .join(', ') +
          ' ]',
      )
      .join(', ') +
    ' ]'
  );
};

const dateTimeMapper = (dateTime: Maybe<DateTime>) =>
  dateTime?.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS) ?? 'NO DATE';

@Entity()
export class RatingsExportDataView extends ExportDataView {
  static override _class = 'RatingsExportDataView';

  static override basicColumnDefinitions = [
    {
      fieldName: 'ratingDoc.ratings.score',
      getFieldMapping: entityInstance => {
        return formatScoresOnRatingDoc(entityInstance.toJSON());
      },
      selectionSet: {
        ratingDoc: {
          ratings: { instructionId: 1, score: 1 },
        },
        osce: {
          instructionCategories: {
            _id: 1,
            instructions: {
              _id: 1,
            },
          },
        },
      } as any,
    },
    {
      fieldName: 'osce.instructionCategories._id',
      getFieldMapping: entityInstance => {
        return '[ ' + entityInstance.osce?.instructionCategories?.map(cat => cat._id).join(', ') + ' ]';
      },
    },
    {
      fieldName: 'osce.instructionCategories.instructions._id',
      getFieldMapping: entityInstance => {
        return (
          '[ ' +
          entityInstance.osce?.instructionCategories
            ?.map(cat => '[ ' + cat.instructions.map(instr => instr._id).join(', ') + ' ]')
            .join(', ') +
          ' ]'
        );
      },
      selectionSet: {
        osce: {
          instructionCategories: {
            _id: 1,
            instructions: {
              _id: 1,
            },
          },
        },
      } as any,
    },
    {
      customID: 'inspectors.ecommerce.fullName',
      columnName: `${brandDictionary.field.inspectors} > ${brandDictionary.field.ecommerce} > ${brandDictionary.field.fullName}`,
      getFieldMapping: entityInstance =>
        entityInstance.inspectors
          .map(ins => (ins?.ecommerce ? `${ins.ecommerce.firstName} ${ins.ecommerce.lastName}` : undefined))
          .filter(Boolean),
      selectionSet: {
        inspectors: {
          ecommerce: {
            firstName: 1,
            lastName: 1,
          },
        },
      } as any,
    },
    {
      customID: 'trainee.ecommerce.fullName',
      columnName: `${brandDictionary.field.trainee} > ${brandDictionary.field.ecommerce} > ${brandDictionary.field.fullName}`,
      getFieldMapping: entityInstance =>
        entityInstance.trainee?.ecommerce
          ? `${entityInstance.trainee.ecommerce.firstName} ${entityInstance.trainee.ecommerce.lastName}`
          : undefined,
      selectionSet: {
        trainee: {
          ecommerce: {
            firstName: 1,
            lastName: 1,
          },
        },
      } as any,
    },
    {
      customID: 'ratingDoc.ratingDuration',
      columnName: `${brandDictionary.field.ratingDoc} > ${brandDictionary.field.ratingDuration}`,
      getFieldMapping: entityInstance => {
        const { updatedAt, createdAt } = entityInstance.ratingDoc;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return !!updatedAt && !!createdAt ? updatedAt.diff(createdAt).toFormat('h:mm:ss') : 'NO DATE';
      },
      selectionSet: {
        ratingDoc: {
          createdAt: 1,
          updatedAt: 1,
        },
      } as any,
    },
    {
      fieldName: 'ratingDoc._class',
      getFieldMapping: entityInstance =>
        RatingsExportDataView._translator.translate('field', 'value', entityInstance.ratingDoc._class),
    },
    {
      fieldName: 'ratingDoc.createdAt',
      getFieldMapping: entityInstance => dateTimeMapper(entityInstance.ratingDoc.createdAt),
    },
    {
      fieldName: 'ratingDoc.updatedAt',
      getFieldMapping: entityInstance => dateTimeMapper(entityInstance.ratingDoc.updatedAt),
    },
    {
      fieldName: 'slot.startTime',
      getFieldMapping: entityInstance => dateTimeMapper(entityInstance.slot.startTime),
    },
    {
      fieldName: 'trainee.profile.wantsNewsletter',
      getFieldMapping: entityInstance => entityInstance.trainee.profile?.wantsNewsletter ?? false,
    },
  ] satisfies ColumnDefinitionBase<typeof RatingsExportLine>[];
}
